import type { IncidentFile, UpdateIncidentFileRequest } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormStack } from '../../components/forms/FormStack';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useUpdateIncidentFileMutation } from '../../graphql/hooks/useUpdateIncidentFileMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';

type FormValues = UpdateIncidentFileRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string().nullish(),
});

interface Props {
  incidentFile: IncidentFile;
}

export const IncidentFileEditModal = createModal<Props>((props) => {
  const { incidentFile, modal } = props;

  const { hide, visible } = modal;
  const { organization, successToast, errorToast } = useAppContext();
  const mutation = useUpdateIncidentFileMutation();

  const initialValues: Partial<FormValues> = {
    name: incidentFile.name,
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          incidentFileId: incidentFile.id,
          incidentId: incidentFile.incidentId,
          request: values,
        })
        .then(async () => {
          hide();
          successToast('File updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating file: ${err.message}`);
        });
    },
    [
      mutation,
      organization.id,
      incidentFile.id,
      incidentFile.incidentId,
      hide,
      successToast,
      errorToast,
    ],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={modal.remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit a file'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <FormStack>
                  <HookedFormField name='name' label='Name' h='full'>
                    <HookedInput name='name' />
                  </HookedFormField>
                </FormStack>
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
